import Api from '@/api'

export default {
  namespaced: true,

  state: {
    items: [],
    item: null,
    meta: null,
  },

  getters: {
    items(state) {
      return state.items
    },
    item(state) {
      return state.item
    },
    meta(state) {
      return state.meta
    },
  },

  mutations: {
    setItems(state, items) {
      state.items = items
    },
    setMeta(state, meta) {
      state.meta = meta
    },
    setItem(state, item) {
      state.item = item
    },
    addItem(state, item) {
      state.items.push(item)
    },
    updateItemInList(state, item) {
      const i = state.items.findIndex((it) => it.id === item.id)
      if (i !== -1) state.items.splice(i, 1, item)
    },
    removeItem(state, id) {
      state.items = state.items.filter((it) => it.id !== id)
    },
  },

  actions: {
    async getPlantEquipment({ commit }, params = {}) {
      const res = await Api.get('/plant-equipment', { params })
      const items = res.data.data || []
      commit('setItems', items)
      commit('setMeta', res.data.meta || null)
      return items
    },

    // Tradie/supervisor-facing dropdown source — unlike getPlantEquipment,
    // this needs no business_admin record (`/plant-equipment` 403s a
    // supervisor tradie via _authorizedBa() even though they hold
    // timesheets.view) and never returns default_rate/rate_override.
    async getMyPlantEquipment({ commit }, params = {}) {
      const res = await Api.get('/plant-equipment/mine', { params })
      const items = res.data.data || []
      commit('setItems', items)
      return items
    },

    async createPlantEquipment({ commit }, payload) {
      const res = await Api.post('/plant-equipment', payload)
      const item = res.data.data
      commit('addItem', item)
      return item
    },

    async updatePlantEquipment({ commit }, { id, ...fields }) {
      const res = await Api.patch(`/plant-equipment/${id}`, fields)
      const item = res.data.data
      commit('updateItemInList', item)
      return item
    },

    // The archive endpoint isn't guaranteed to echo the full updated record,
    // so flip is_archived locally on the matching item rather than trusting
    // the response body to be the record.
    async archivePlantEquipment({ commit, state }, id) {
      await Api.delete(`/plant-equipment/${id}`)
      const existing = state.items.find((it) => it.id === id)
      if (existing) {
        commit('updateItemInList', { ...existing, is_archived: true })
      }
    },

    // Mirrors archivePlantEquipment's local-flip approach — the restore
    // endpoint does echo the full record, but flip locally for consistency
    // and to avoid a redundant re-render dependency on the response shape.
    async restorePlantEquipment({ commit, state }, id) {
      await Api.post(`/plant-equipment/${id}/restore`)
      const existing = state.items.find((it) => it.id === id)
      if (existing) {
        commit('updateItemInList', { ...existing, is_archived: false })
      }
    },

    async forceDeletePlantEquipment({ commit }, id) {
      await Api.delete(`/plant-equipment/${id}/permanent`)
      commit('removeItem', id)
    },

    async bulkDeletePlantEquipment({ commit }, ids) {
      const res = await Api.post('/plant-equipment/bulk-delete', { ids })
      const result = res.data.data
      ids.forEach((id) => {
        const stillFailed = (result.errors || []).some((e) => e.id === id)
        if (!stillFailed) commit('removeItem', id)
      })
      return result
    },

    async importPlantEquipmentCsv(
      _,
      { file, projectIds = [], applyToAll = false },
    ) {
      const formData = new FormData()
      formData.append('csv_file', file)
      // Must be the string '1'/'0' — FormData stringifies a boolean to
      // "true"/"false", which Laravel's `boolean` rule rejects outright.
      formData.append('apply_to_all_projects', applyToAll ? '1' : '0')
      projectIds.forEach((id) => formData.append('project_ids[]', id))
      const res = await Api.post('/plant-equipment/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data.data
    },

    async amendPlantEntry(_, { id, quantity }) {
      const res = await Api.patch(`/timesheet-plant-entries/${id}`, {
        quantity,
      })
      return res.data.data
    },

    async getPlantEntryAmendments(_, id) {
      const res = await Api.get(`/timesheet-plant-entries/${id}/amendments`)
      return res.data.data || []
    },

    async getPlantCostReport(_, params) {
      const res = await Api.get('/reports/plant-costs', { params })
      return res.data.data
    },

    async exportPlantCostReport(_, params) {
      const res = await Api.get('/reports/plant-costs/export', {
        params,
        responseType: 'blob',
      })
      return res.data
    },
  },
}
